import {
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
  CircularProgress,
  PopoverOrigin,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ChevronDownIcon, PlusIcon } from 'src/components/icons';
import { MenuVariant } from 'src/constants';
import { Action } from 'src/legacy/components/Dropdowns';
import { BaseMenu } from 'src/legacy/components/Dropdowns/BaseMenu';
import { HoverBackground } from 'src/theme/colors';
import ColorUtils from 'src/utils/ColorUtils';

interface StyleProps {
  isSecondaryButton: boolean;
}

const useCreateButtonStyles = makeStyles<StyleProps>()((theme, props) => ({
  createButton: {
    padding: `0 ${theme.spacing(1)}`,
  },
  createButtonActive: {
    // background color of button based on button variant (primary or secondary)
    // when dropdown menu is open
    backgroundColor: props.isSecondaryButton
      ? HoverBackground
      : ColorUtils.GetColorDarknessShades(theme.palette.primary.main).midDark,
  },
  buttonProgress: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    marginTop: -15,
    marginLeft: -15,
  },
}));

export interface DropDownItemMoreAction {
  name: string;
  icon: React.ReactElement;
  // when clicked on sub action
  // we will get parent menu item information in param
  onClick: (action: Action) => void;
}

export interface DropDownOption {
  value: string;
  label: string;
  // these will be the sub actions we want on the the dropdown menu item
  subActions?: DropDownItemMoreAction[];
}

export interface MainOption {
  label: string;
  icon: JSX.Element;
  disabled?: boolean;
  tooltip?: string;
  hasDivider?: boolean;
}

export interface CreateButtonProps {
  htmlId: string;
  caretOptions: DropDownOption[];
  dropDownTitle: string;
  actionOptionTitle: string;
  mainOptions: Array<MainOption>;
  headerOptions?: Array<MainOption>;
  onClickOption: (option: any) => void;
  onClickOptionAction: () => void;
  isUploading?: boolean;
  iconOnly?: boolean;
  forceShowLabel?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  buttonGroupProps?: ButtonGroupProps;
  dropdownVariant?: MenuVariant;
  hasStartIcon?: boolean;
  disabledTooltip?: JSX.Element | string;
  /** When true, shows both icon and label on mobile screens */
  showPlusIcon?: boolean;
}

export const defaultCreateButtonProps: CreateButtonProps = {
  htmlId: 'default-create-button-id',
  caretOptions: [],
  dropDownTitle: 'Create',
  actionOptionTitle: 'Create New',
  mainOptions: [],
  onClickOption: () => {},
  onClickOptionAction: () => {},
  isUploading: false,
  iconOnly: false,
  forceShowLabel: false,
  dropdownVariant: 'regular',
  hasStartIcon: true,
  showPlusIcon: false,
};

export type CreateButtonCombinedProps = ButtonProps &
  Partial<CreateButtonProps>;

export const CreateButton: React.FC<CreateButtonCombinedProps> = React.memo(
  ({
    children,
    htmlId,
    caretOptions,
    mainOptions,
    headerOptions,
    dropDownTitle,
    actionOptionTitle,
    isUploading = false,
    onClickOption,
    onClickOptionAction,
    iconOnly = false,
    forceShowLabel = false,
    anchorOrigin,
    transformOrigin,
    onClick,
    buttonGroupProps,
    dropdownVariant = 'wide',
    hasStartIcon = true,
    disabledTooltip = '',
    showPlusIcon = false,
    ...rest
  }) => {
    const isSecondaryButton = buttonGroupProps?.color === 'secondary';
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const { classes } = useCreateButtonStyles({ isSecondaryButton });
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const formatOptions = (options: MainOption[]): Action[] =>
      options.map(({ label, icon, disabled, tooltip, hasDivider }) => ({
        key: label,
        onClick: () => onClickOption?.(label),
        name: label,
        icon,
        isDisabled: disabled,
        tooltipLabel: tooltip,
        hasDivider,
      }));

    const getFormattedHeaderActions = React.useCallback((): Action[] => {
      if (headerOptions) {
        return formatOptions(headerOptions);
      }
      return [];
    }, [headerOptions, onClickOption]);

    const getFormattedActions = React.useCallback((): Action[] => {
      if (mainOptions) {
        return formatOptions(mainOptions);
      }

      if (caretOptions) {
        return caretOptions?.map(
          (option): Action => ({
            key: `${option.value || option.label}`,
            onClick: () => {
              onClickOption?.(option.value);
            },
            name: option.label,
            subActions: option.subActions?.map(
              (subAction): Action => ({
                ...subAction,
                name: subAction.name,
                icon: subAction.icon,
                onClick: () => {
                  subAction.onClick({
                    name: option.label,
                    onClick: () => {},
                  });
                },
              }),
            ),
          }),
        );
      }
      return [];
    }, [caretOptions, mainOptions, onClickOption]);

    return (
      <div>
        <Tooltip title={disabledTooltip}>
          <ButtonGroup
            disableRipple
            disableFocusRipple
            variant="contained"
            color="primary"
            aria-label="split button"
            {...buttonGroupProps}
          >
            <Button
              color={buttonGroupProps?.color || 'primary'}
              className={classNames(classes.createButton, {
                [classes.createButtonActive]: open, // when popover is open add hover styles
              })}
              id={htmlId}
              {...(!iconOnly
                ? {
                    startIcon:
                      (!isMobile || showPlusIcon) && hasStartIcon ? (
                        <PlusIcon />
                      ) : null,
                  }
                : {})}
              onClick={onClick || handleClick}
              disabled={isUploading}
              {...rest}
            >
              {(isMobile || iconOnly) && !forceShowLabel && !showPlusIcon ? (
                <PlusIcon />
              ) : (
                children
              )}
              {isUploading && (
                <CircularProgress
                  className={classes.buttonProgress}
                  size={30}
                />
              )}
            </Button>
            {caretOptions && (
              <Button
                color="primary"
                size="small"
                aria-label="caret options"
                aria-controls="caret-options-menu"
                aria-haspopup="true"
                className={classNames(classes.createButton, {
                  [classes.createButtonActive]: open,
                })}
                onClick={handleClick}
              >
                <ChevronDownIcon />
              </Button>
            )}
          </ButtonGroup>
        </Tooltip>
        <BaseMenu
          variant={dropdownVariant}
          menuTitle={dropDownTitle}
          addOptionLabel={actionOptionTitle}
          onAddOption={onClickOptionAction}
          menuProps={{
            id: 'options-menu',
            anchorEl,
            keepMounted: true,
            open,
            onClose: handleClose,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
              ...anchorOrigin,
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
              ...transformOrigin,
            },
          }}
          headerActions={getFormattedHeaderActions()}
          actions={getFormattedActions()}
          preventClickEventPropagation
        />
      </div>
    );
  },
);
